import { normalize } from 'normalizr';
import jsonToFormData from 'json-form-data';
import { stringifyUrl } from 'query-string';

import { topicSchema } from 'models/topic';
import { ThunkActionCreator } from 'app/store';
import { get, post, deleteResource } from 'utils/api';
import {
  getSlideContentType,
  normalizeSlideFileForUpload,
} from 'utils/slideFile';
import {
  Document,
  LinkAttachment,
  linkAttachmentSchema,
  documentSchema,
} from 'models/document';
import { addRecords, deleteRecords } from 'slices/entities';
import { MAX_ATTACHMENT_FILE_SIZE } from 'constants/fileUpload';

interface UploadSlidePayload {
  topicId: string;
  file: File;
  name: string;
}

interface UploadSlideResponse extends Document {
  lesson_id: string;
}

export const uploadSlide: ThunkActionCreator<UploadSlidePayload> = ({
  topicId,
  file,
  name,
}) => async dispatch => {
  const normalizedFile = await normalizeSlideFileForUpload(file);

  if (normalizedFile.size > MAX_ATTACHMENT_FILE_SIZE) {
    throw new Error('FILE_SIZE_EXCEEDED');
  }

  const contentType = getSlideContentType(normalizedFile);

  // fetch S3 presigned POST
  const fetchUploadFormValuesRes = await get(
    stringifyUrl({
      url: `/content/topics/${topicId}/slides/upload_form_values.json`,
      query: { content_type: contentType, filename: normalizedFile.name },
    }),
  );

  if (fetchUploadFormValuesRes.status >= 400) {
    throw fetchUploadFormValuesRes.status;
  }

  const { url, form_fields: fields } = await fetchUploadFormValuesRes.json();

  // upload file to S3
  const s3UploadRes = await fetch(url, {
    method: 'POST',
    // It's important that the file is last or else S3 returns the error:
    // "Bucket POST must contain a field named 'key'"
    body: jsonToFormData({ ...fields, file: normalizedFile }),
  });

  if (s3UploadRes.status >= 400) {
    throw s3UploadRes.status;
  }

  const xml = await s3UploadRes.text();
  const parser = new DOMParser();
  const parsed = parser.parseFromString(xml, 'text/html');
  const keyElement = parsed.getElementsByTagName('Key')[0];
  const key = keyElement?.childNodes[0]?.nodeValue;

  if (!key) {
    throw new Error('S3_KEY_PARSE_ERROR');
  }

  const res = await post(`/content/topics/${topicId}/slides.json`, {
    file_key: key,
    filename: normalizedFile.name,
    name,
  });

  if (res.status >= 400) {
    throw res.status;
  } else {
    const {
      lesson_id,
      ...newDocument
    } = (await res.json()) as UploadSlideResponse;

    const updatedTopic = normalize(
      {
        id: topicId,
        lesson: {
          id: lesson_id,
          documents: [newDocument],
        },
      },
      topicSchema,
    );

    await dispatch(
      addRecords({
        ...updatedTopic,
        // Concat chapters too since normalized lesson object always has a
        // chapters property which is [] in this case (see lessonSchema
        // processStrategy). Otherwise, the lesson chapters will become empty.
        options: { concatKeys: ['documents', 'chapters'] },
      }),
    );
  }
};

interface AddSlideLinkAttachmentPayload {
  topicId: string;
  name: string;
  url: string;
}

interface AddSlideLinkAttachmentResponse extends LinkAttachment {
  lesson_id: string;
}

export const addSlideLinkAttachment: ThunkActionCreator<AddSlideLinkAttachmentPayload> = ({
  topicId,
  name,
  url,
}) => async dispatch => {
  const res = await post(`/content/topics/${topicId}/slides/create_link`, {
    name,
    url,
  });

  if (res.status >= 400) {
    throw res.status;
  } else {
    const {
      lesson_id,
      ...newLinkAttachment
    } = (await res.json()) as AddSlideLinkAttachmentResponse;

    const updatedTopic = normalize(
      {
        id: topicId,
        lesson: {
          id: lesson_id,
          link_attachments: [newLinkAttachment],
        },
      },
      topicSchema,
    );

    await dispatch(
      addRecords({
        ...updatedTopic,
        // Concat chapters too since normalized lesson object always has a
        // chapters property which is [] in this case (see lessonSchema
        // processStrategy). Otherwise, the lesson chapters will become empty.
        options: { concatKeys: ['link_attachments', 'chapters'] },
      }),
    );

    return newLinkAttachment as LinkAttachment;
  }
};

interface DeleteSlideAttachmentPayload {
  topicId: string;
  slideId: string;
  type: 'file' | 'link';
}

export const deleteSlideAttachment: ThunkActionCreator<DeleteSlideAttachmentPayload> = ({
  topicId,
  slideId,
  type,
}) => async dispatch => {
  const res = await deleteResource(
    `/content/topics/${topicId}/slides/links/${slideId}`,
  );

  let schema;

  if (type === 'file') {
    schema = documentSchema;
  } else if (type === 'link') {
    schema = linkAttachmentSchema;
  } else {
    throw new Error('Invalid attachment type');
  }

  if (res.status >= 400) {
    throw res.status;
  } else {
    await dispatch(deleteRecords(normalize({ id: slideId }, schema)));
  }
};

interface ValidateGoogleSlidesUrlPayload {
  topicId: string;
  url: string;
}

export const validateGoogleSlidesUrl: ThunkActionCreator<ValidateGoogleSlidesUrlPayload> = ({
  topicId,
  url,
}) => async () => {
  const res = await get(
    stringifyUrl({
      url: `/content/topics/${topicId}/slides/validate_google_slides_url`,
      query: { url },
    }),
  );

  if (res.status >= 400) {
    throw res.status;
  }

  return res.json();
};
